import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    Calendar,
    CheckCircle2,
    Clock,
    CreditCard,
    Download,
    Euro,
    FileText,
    Info,
    Loader2,
    RefreshCw,
    Shield,
    TimerOff,
    XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import moment from "moment";
import { Link } from "react-router-dom";
import { AuthContext, IntlContext } from "context";
import type { AdApplicationInterface } from "types/ad";
import { AdApplyStatus, AdFrequency } from "types/ad";
import { CodeCesu } from "types/cesu";
import {
    activateAici,
    activateCesuPlus,
    checkCesuNumPe,
    checkCesuNumSp,
    generateCesuAttestation,
} from "hooks/cesu";
import Radio from "ui/Form/Element/Radio";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import { getPayloadErrorFromResponse } from "lib/serverError";
import CalculOpperator from "helpers/CalculOpperator";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { adKeys } from "hooks/ad";
import { ROUTES } from "routes";
import OfferPaymentModal from "./OfferPaymentModal";

const CESU_NO_LINK_ERROR_PREFIX = "Il n'existe aucun lien";
const CESU_PLUS_BLOCKED_ERROR_PREFIX = "Blocage Cesu+ pour le sala";
const AICI_INVALID_COMMUNE_ERROR_PREFIX = "codeCommuneNaissance est invalide";

const showCesuActivationErrors = (
    err: unknown,
    formatMessage: (key: string) => string,
): void => {
    const error = getPayloadErrorFromResponse(err)?.description ?? "";

    toast.error(
        formatMessage("our_services.booking.confirm.message.transmitting_cesu.error") +
            (error ? ` Raison : "${error}"` : ""),
    );

    if (error.startsWith(CESU_PLUS_BLOCKED_ERROR_PREFIX)) {
        toast.error(
            "Impossible d'activer le CESU+ : le salarié n'a pas encore renseigné ses coordonnées bancaires sur son compte CESU.",
        );
    }

    if (error.startsWith(AICI_INVALID_COMMUNE_ERROR_PREFIX)) {
        toast.error(
            "Impossible d'activer l'Avance Immédiate de Crédit d'Impôt pour le moment : veuillez activer ce service directement depuis votre compte CESU.",
        );
    }
};

const formatEuro = (value: number): string => `${value.toFixed(2)} €`;

const isApplicationCesuPlusActive = (application: AdApplicationInterface): boolean =>
    application.activate_cesu_plus === 1 ||
    application.activate_cesu_plus === "1" ||
    application.activate_cesu_plus === true ||
    Boolean(application.cesu_plus_status);

const OFFER_EXPIRATION_DAYS = 7;

const formatOfferRemainingTime = (remainingMs: number): string => {
    if (remainingMs <= 0) {
        return "0 min";
    }

    const totalMinutes = Math.ceil(remainingMs / 60_000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) {
        return `${days}j ${hours}h`;
    }

    if (hours > 0) {
        return `${hours}h ${minutes}min`;
    }

    return `${minutes} min`;
};

const getOfferRemainingMs = (createdAt: string): number =>
    moment(createdAt).add(OFFER_EXPIRATION_DAYS, "days").valueOf() - Date.now();

type OfferExpirationCountdownProps = {
    createdAt: string;
};

const OfferExpirationCountdown: React.FC<OfferExpirationCountdownProps> = ({ createdAt }) => {
    const intl = useContext(IntlContext);
    const [remainingLabel, setRemainingLabel] = useState(() =>
        formatOfferRemainingTime(getOfferRemainingMs(createdAt)),
    );

    useEffect(() => {
        const update = () => {
            setRemainingLabel(formatOfferRemainingTime(getOfferRemainingMs(createdAt)));
        };

        update();
        const interval = window.setInterval(update, 60_000);

        return () => window.clearInterval(interval);
    }, [createdAt]);

    return (
        <div className="flex shrink-0 items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5">
            <Clock className="size-3.5 text-amber-700" aria-hidden />
            <span className="whitespace-nowrap text-[11px] font-bold text-amber-700">
                {intl.formatMessage("booking.messages.confirmation.expiration_countdown", {
                    remaining: remainingLabel,
                })}
            </span>
        </div>
    );
};

type AcceptOptions = {
    activate_cesu_plus: string;
    cesu_plus_status: boolean;
    aici_status: boolean;
};

type Props = {
    application: AdApplicationInterface;
    isEmployerView?: boolean;
    onAccept?: (options: AcceptOptions) => void | Promise<void>;
    onRefuse?: () => void;
};

type OfferCardTheme = {
    headerBgClass: string;
    cardBorderClass: string;
    subHeaderBorderClass: string;
    subHeaderTitleClass: string;
    subHeaderSubtitleClass: string;
    subHeaderIconClass: string;
    gridCardClass: string;
    gridIconClass: string;
    gridLabelClass: string;
};

type OfferCardState = OfferCardTheme & {
    HeaderIcon: LucideIcon;
    SubHeaderIcon: LucideIcon;
    headerTitleId: string;
    badgeLabelId: string;
    subHeaderTitleId: string;
    subHeaderSubtitleId: string;
};

const successTheme: OfferCardTheme = {
    headerBgClass: "bg-[#00bc7d]",
    cardBorderClass: "border-[#a4f4cf] bg-gradient-to-br from-[#ecfdf5] to-[#f0fdf9]",
    subHeaderBorderClass: "border-[#a4f4cf]",
    subHeaderTitleClass: "text-[#004f3b]",
    subHeaderSubtitleClass: "text-[#007a55]",
    subHeaderIconClass: "text-[#007a55]",
    gridCardClass: "flex flex-col gap-2 rounded-2xl border border-[#a4f4cf] bg-white px-[17px] py-[17px]",
    gridIconClass: "text-[#007a55]",
    gridLabelClass: "text-[11px] font-black uppercase tracking-wide text-[#004f3b]",
};

const pendingTheme: OfferCardTheme = {
    headerBgClass: "bg-indigo-600",
    cardBorderClass: "border-indigo-100 bg-gradient-to-br from-indigo-50 to-violet-50",
    subHeaderBorderClass: "border-indigo-100",
    subHeaderTitleClass: "text-indigo-600",
    subHeaderSubtitleClass: "text-indigo-600",
    subHeaderIconClass: "text-indigo-600",
    gridCardClass: "flex flex-col gap-2 rounded-2xl border border-indigo-100 bg-white px-[17px] py-[17px]",
    gridIconClass: "text-indigo-600",
    gridLabelClass: "text-[11px] font-black uppercase tracking-wide text-indigo-600",
};

const dangerTheme: OfferCardTheme = {
    headerBgClass: "bg-red-600",
    cardBorderClass: "border-red-200 bg-gradient-to-br from-red-50 to-rose-50",
    subHeaderBorderClass: "border-red-200",
    subHeaderTitleClass: "text-red-800",
    subHeaderSubtitleClass: "text-red-600",
    subHeaderIconClass: "text-red-600",
    gridCardClass: "flex flex-col gap-2 rounded-2xl border border-red-200 bg-white px-[17px] py-[17px]",
    gridIconClass: "text-red-600",
    gridLabelClass: "text-[11px] font-black uppercase tracking-wide text-red-800",
};

const inactiveTheme: OfferCardTheme = {
    headerBgClass: "bg-[#6b7280]",
    cardBorderClass: "border-[#d1d5db] bg-gradient-to-br from-[#f9fafb] to-[#f3f4f6]",
    subHeaderBorderClass: "border-[#d1d5db]",
    subHeaderTitleClass: "text-[#374151]",
    subHeaderSubtitleClass: "text-[#6b7280]",
    subHeaderIconClass: "text-[#6b7280]",
    gridCardClass: "flex flex-col gap-2 rounded-2xl border border-[#d1d5db] bg-white px-[17px] py-[17px]",
    gridIconClass: "text-[#6b7280]",
    gridLabelClass: "text-[11px] font-black uppercase tracking-wide text-[#374151]",
};

const getOfferCardState = (status: AdApplyStatus, canAct: boolean, isCancelled: boolean): OfferCardState => {
    if (isCancelled) {
        return {
            ...dangerTheme,
            HeaderIcon: XCircle,
            SubHeaderIcon: XCircle,
            headerTitleId: "booking.messages.confirmation.offer_cancelled",
            badgeLabelId: "booking.messages.confirmation.badge_cancelled",
            subHeaderTitleId: "booking.messages.confirmation.cancelled_title",
            subHeaderSubtitleId: "booking.messages.confirmation.cancelled_subtitle",
        };
    }

    if (status === AdApplyStatus.EXPIRED) {
        return {
            ...inactiveTheme,
            HeaderIcon: TimerOff,
            SubHeaderIcon: TimerOff,
            headerTitleId: "booking.messages.confirmation.offer_expired",
            badgeLabelId: "booking.messages.confirmation.badge_expired",
            subHeaderTitleId: "booking.messages.confirmation.expired_title",
            subHeaderSubtitleId: "booking.messages.confirmation.expired_subtitle",
        };
    }

    if (status === AdApplyStatus.REFUSED) {
        return {
            ...inactiveTheme,
            HeaderIcon: XCircle,
            SubHeaderIcon: XCircle,
            headerTitleId: "booking.messages.confirmation.offer_refused",
            badgeLabelId: "booking.messages.confirmation.badge_refused",
            subHeaderTitleId: "booking.messages.confirmation.refused_title",
            subHeaderSubtitleId: "booking.messages.confirmation.refused_subtitle",
        };
    }

    if (status === AdApplyStatus.PENDING && canAct) {
        return {
            ...pendingTheme,
            HeaderIcon: Clock,
            SubHeaderIcon: Clock,
            headerTitleId: "booking.messages.confirmation.offer_received",
            badgeLabelId: "booking.messages.confirmation.badge_pending",
            subHeaderTitleId: "booking.messages.confirmation.pending_title",
            subHeaderSubtitleId: "booking.messages.confirmation.pending_subtitle",
        };
    }

    if (status === AdApplyStatus.PENDING) {
        return {
            ...pendingTheme,
            HeaderIcon: Clock,
            SubHeaderIcon: Clock,
            headerTitleId: "booking.messages.confirmation.offer_sent",
            badgeLabelId: "booking.messages.confirmation.badge_pending",
            subHeaderTitleId: "booking.messages.confirmation.pending_title_provider",
            subHeaderSubtitleId: "booking.messages.confirmation.pending_subtitle_provider",
        };
    }

    return {
        ...successTheme,
        HeaderIcon: CheckCircle2,
        SubHeaderIcon: FileText,
        headerTitleId: "booking.messages.confirmation.offer_accepted",
        badgeLabelId: "booking.messages.confirmation.badge_confirmed",
        subHeaderTitleId: "booking.messages.confirmation.title",
        subHeaderSubtitleId: "booking.messages.confirmation.subtitle",
    };
};

const isApplicationBookingCancelled = (application: AdApplicationInterface): boolean =>
    Boolean(application.booking?.cancelled_at ?? application.ad?.booking?.cancelled_at) ||
    application.status === AdApplyStatus.CANCELLED;

const BookingConfirmationCard: React.FC<Props> = ({
    application,
    isEmployerView = false,
    onAccept,
    onRefuse,
}) => {
    const intl = useContext(IntlContext);
    const { account } = useContext(AuthContext);

    const [hasCesuSPValid, setHasCesuSPValid] = useState(false);
    const [hasCesuPEValid, setHasCesuPEValid] = useState(false);
    const [hasCesuValid, setHasCesuValid] = useState(false);
    const [hasErrCesu, setHasErrCesu] = useState(false);
    const [hasErrCesuSalarie, setHasErrCesuSalarie] = useState(false);
    const [hasCesuPlusActivated, setHasCesuPlusActivated] = useState(false);
    const [hasAiciActivated, setHasAiciActivated] = useState(false);
    const [cesuPlusStatus, setCesuPlusStatus] = useState(() => Boolean(application.cesu_plus_status));
    const [aiciStatus, setAiciStatus] = useState(() => Boolean(application.aici_status));
    const [activateCesuPlusChoice, setActivateCesuPlusChoice] = useState<string>("0");
    const [attestIsLoading, setAttestIsLoading] = useState(false);
    const [attestIsGenerated, setAttestIsGenerated] = useState(false);
    const [attestHasError, setAttestHasError] = useState(false);
    const [isAccepting, setIsAccepting] = useState(false);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [isPaid, setIsPaid] = useState(Boolean(application.paid));
    const queryClient = useQueryClient();

    const isPending = application.status === AdApplyStatus.PENDING;
    const isAccepted = application.status === AdApplyStatus.ACCEPTED;
    const isBookingCancelled = isApplicationBookingCancelled(application);
    const canAct = isPending && (Boolean(onAccept) || Boolean(onRefuse)) && !isBookingCancelled;

    const bookingId = application.id ?? application.booking?.id ?? application.ad?.booking?.id;
    const providerId = application.provider_id ?? application.provider?.id;
    const providerCesu = application.provider?.num_cesu;
    const clientCesu = account?.resources?.client?.[0];

    const startTime = application.start_time?.slice(0, 5);
    const startDate = !application.start_date
        ? intl.formatMessage("booking.messages.confirmation.start_date_asap")
        : startTime
          ? intl.formatMessage("booking.messages.confirmation.start_date_time", {
                date: moment(application.start_date).locale("fr").format("dddd D MMMM"),
                time: startTime,
            })
          : moment(application.start_date).locale("fr").format("dddd D MMMM");

    const missionTypeLabel =
        application.mission_type === AdFrequency.RECURRING
            ? intl.formatMessage("ad.card.frequency_recurring")
            : intl.formatMessage("ad.card.frequency_one_time");

    const timestamp = moment(application.updated_at).format("HH:mm");
    const cardState = getOfferCardState(application.status, canAct, isBookingCancelled);
    const HeaderIcon = cardState.HeaderIcon;
    const SubHeaderIcon = cardState.SubHeaderIcon;
    /*const gridCardClass = isPending
        ? "flex flex-col gap-2 rounded-2xl border border-indigo-100 bg-white px-[17px] py-[17px]"
        : "flex flex-col gap-2 rounded-2xl border border-[#d0fae5] bg-white px-[17px] py-[17px]";
    const gridIconClass = isPending ? "text-indigo-600" : "text-[#009966]";
    const gridLabelClass = isPending
        ? "text-[11px] font-black uppercase tracking-wide text-indigo-600"
        : "text-[11px] font-black uppercase tracking-wide text-[#096]";*/

    const needsCesuChoice = hasCesuValid && (!hasCesuPlusActivated || !hasAiciActivated);
    const acceptBlockedByAttestation =
        needsCesuChoice && activateCesuPlusChoice === "1" && !hasCesuPlusActivated && !attestIsGenerated;
    const canSubmitAccept = !acceptBlockedByAttestation && !isAccepting;

    const pricing = useMemo(() => {
        const salaryAmount = Number(application.amount);
        const employerContributions = Number(application.employer_contributions ?? 0);
        const employeeContributions = Number(application.employee_contributions_ef ?? 0);
        const socialContributions = employerContributions + employeeContributions;

        // Commission and management fees apply on the salary only, never on social contributions.
        const [managementFees, salaryWithManagementFees] = new CalculOpperator(null).computeTTC(salaryAmount);

        const taxCredit = (salaryAmount + socialContributions) / 2;
        const netFinalCost = salaryAmount + socialContributions + managementFees - taxCredit;
        const cesuPlusSelected =
            hasCesuPlusActivated ||
            (canAct && activateCesuPlusChoice === "1") ||
            (isAccepted && isApplicationCesuPlusActive(application));
        const aiciActive =
            hasAiciActivated ||
            aiciStatus ||
            (canAct && activateCesuPlusChoice === "1") ||
            (isAccepted && Boolean(application.aici_status));
        const amountToPayNow = cesuPlusSelected ? managementFees : salaryWithManagementFees;

        return {
            salaryAmount,
            employerContributions,
            employeeContributions,
            socialContributions,
            managementFees,
            taxCredit,
            netFinalCost,
            amountToPayNow,
            cesuPlusSelected,
            aiciActive,
            hasContributions: socialContributions > 0,
        };
    }, [
        application,
        activateCesuPlusChoice,
        canAct,
        hasCesuPlusActivated,
        hasAiciActivated,
        aiciStatus,
        isAccepted,
    ]);

    const clientId = application.client_id ?? application.ad?.client_id;
    const showPayButton = isAccepted && !isBookingCancelled && isEmployerView && !isPaid && Boolean(bookingId) && Boolean(providerId) && Boolean(clientId);

    const handlePaymentSuccess = async () => {
        setIsPaid(true);
        await queryClient.invalidateQueries({ queryKey: adKeys.applications() });
        await queryClient.invalidateQueries({ queryKey: adKeys.ourApplications() });
    };

    useEffect(() => {
        setIsPaid(Boolean(application.paid));
    }, [application.paid]);

    useEffect(() => {
        if (!isEmployerView || (!canAct && !isAccepted)) return;

        if (
            clientCesu?.code_cesu &&
            clientCesu.code_cesu !== CodeCesu.WITHOUT_CESU &&
            clientCesu.num_cesu &&
            providerCesu
        ) {
            checkCesuNumPe(clientCesu.num_cesu, clientCesu.birthday, providerCesu)
                .then((response) => {
                    if (response?.inscrit && response.mandatPresent) {
                        setHasCesuPEValid(response.inscrit);
                    }
                    if (response.etatCesuPlus === "activé") {
                        setHasCesuPlusActivated(true);
                    }
                    if (response.etatAICI) {
                        setHasAiciActivated(true);
                    }
                })
                .catch((err: unknown) => {
                    const description = getPayloadErrorFromResponse(err)?.description ?? "";

                    if (description.startsWith(CESU_NO_LINK_ERROR_PREFIX)) {
                        setHasErrCesu(true);
                    }
                });
        }

        if (providerId) {
            checkCesuNumSp(providerId).then((response) => {
                if (response?.inscrit) {
                    setHasCesuSPValid(response.inscrit);
                } else {
                    setHasErrCesuSalarie(true);
                }
            });
        }
    }, [canAct, isAccepted, isEmployerView, clientCesu, providerCesu, providerId]);

    useEffect(() => {
        setCesuPlusStatus(Boolean(application.cesu_plus_status));
    }, [application.cesu_plus_status]);

    useEffect(() => {
        setAiciStatus(Boolean(application.aici_status));
    }, [application.aici_status]);

    useEffect(() => {
        if (hasCesuSPValid && hasCesuPEValid) {
            setHasCesuValid(true);

            if (!hasCesuPlusActivated) {
                setActivateCesuPlusChoice("0");
            } else {
                setCesuPlusStatus(true);
            }

            if (!hasAiciActivated) {
                setActivateCesuPlusChoice("0");
            } else {
                setAiciStatus(true);
            }
        }
    }, [hasCesuSPValid, hasCesuPEValid, hasCesuPlusActivated, hasAiciActivated]);

    const generateAttestation = async () => {
        if (!bookingId) return;

        try {
            setAttestIsLoading(true);
            await generateCesuAttestation(bookingId);
            setAttestIsLoading(false);
            setAttestIsGenerated(true);
        } catch {
            setAttestIsLoading(false);
            setAttestHasError(true);
        }
    };

    const handleAcceptClick = async () => {
        if (!onAccept || !canSubmitAccept) return;

        setIsAccepting(true);

        try {
            const activateCesuPlusValue = hasCesuPlusActivated
                ? "0"
                : activateCesuPlusChoice === "1"
                  ? "1"
                  : "0";
            const shouldActivateCesuServices =
                activateCesuPlusChoice === "1" && !hasCesuPlusActivated;
            const cesuPlusStatusValue = hasCesuPlusActivated || cesuPlusStatus;
            const aiciStatusValue = hasAiciActivated || aiciStatus || shouldActivateCesuServices;

            if (shouldActivateCesuServices && bookingId && providerCesu) {
                const routes: Promise<unknown>[] = [];
                routes.push(activateCesuPlus(bookingId));
                if (!hasAiciActivated) {
                    routes.push(activateAici(bookingId));
                }
                try {
                    await Promise.all(routes);
                } catch (err: unknown) {
                    if (getPayloadErrorFromResponse(err)) {
                        showCesuActivationErrors(err, intl.formatMessage);
                        return;
                    }
                    throw err;
                }
            }

            await onAccept({
                activate_cesu_plus: activateCesuPlusValue,
                cesu_plus_status: cesuPlusStatusValue,
                aici_status: aiciStatusValue,
            });
        } catch (err: unknown) {
            toast.error(intl.formatMessage("generic.error"));
        } finally {
            setIsAccepting(false);
        }
    };

    return (
        <div
            className={`mx-auto max-w-[600px] w-full my-3 overflow-hidden rounded-3xl border-2 ${cardState.cardBorderClass} shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)]`}
        >
            {/* Header */}
            <div className={`flex h-[72px] items-center justify-between ${cardState.headerBgClass} px-6`}>
                <div className="flex items-center gap-3">
                    <div className="flex size-10 flex-shrink-0 items-center justify-center rounded-full bg-white/20">
                        <HeaderIcon className="size-6 text-white" />
                    </div>
                    <div>
                        <p className="text-[15px] font-black leading-[22.5px] text-white">
                            {intl.formatMessage(cardState.headerTitleId)}
                        </p>
                        <p className="text-[12px] font-medium leading-[18px] text-white/80">{timestamp}</p>
                    </div>
                </div>
                <div className="flex h-9 items-center rounded-full bg-white/20 px-4">
                    <span className="text-[11px] font-black uppercase tracking-[0.55px] text-white">
                        {intl.formatMessage(cardState.badgeLabelId)}
                    </span>
                </div>
            </div>

            {/* Content */}
            <div className="flex flex-col gap-4 px-6 pt-6 pb-4">
                {/* Sub-header */}
                <div className={`flex items-center justify-between gap-3 border-b ${cardState.subHeaderBorderClass} pb-4`}>
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex size-12 flex-shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm">
                            <SubHeaderIcon className={`size-6 ${cardState.subHeaderIconClass}`} />
                        </div>
                        <div className="flex min-w-0 flex-col gap-1">
                            <p className={`text-[13px] font-bold leading-[19.5px] ${cardState.subHeaderTitleClass}`}>
                                {intl.formatMessage(cardState.subHeaderTitleId)}
                            </p>
                            <p className={`text-[12px] font-normal leading-[18px] ${cardState.subHeaderSubtitleClass}`}>
                                {intl.formatMessage(cardState.subHeaderSubtitleId)}
                            </p>
                        </div>
                    </div>
                    {isPending ? <OfferExpirationCountdown createdAt={application.created_at} /> : null}
                </div>

                {/* Grid */}
                <div className="grid grid-cols-2 gap-4">
                    <div className={cardState.gridCardClass}>
                        <div className="flex items-center gap-2">
                            <RefreshCw className={`size-4 ${cardState.gridIconClass}`} />
                            <span className={cardState.gridLabelClass}>
                                {intl.formatMessage("booking.messages.confirmation.type")}
                            </span>
                        </div>
                        <p className="text-[14px] font-black text-[#1a1c2d]">{missionTypeLabel}</p>
                    </div>

                    <div className={cardState.gridCardClass}>
                        <div className="flex items-center gap-2">
                            <Calendar className={`size-4 ${cardState.gridIconClass}`} />
                            <span className={cardState.gridLabelClass}>
                                {intl.formatMessage("booking.messages.confirmation.start")}
                            </span>
                        </div>
                        <p className="text-[14px] font-black text-[#1a1c2d]">{startDate}</p>
                    </div>

                    <div className={cardState.gridCardClass}>
                        <div className="flex items-center gap-2">
                            <Clock className={`size-4 ${cardState.gridIconClass}`} />
                            <span className={cardState.gridLabelClass}>
                                {intl.formatMessage("booking.messages.confirmation.duration")}
                            </span>
                        </div>
                        <p className="text-[14px] font-black text-[#1a1c2d]">{application.duration}h</p>
                    </div>

                    <div className="relative rounded-2xl border border-[#4c4cf4] bg-[#4c4cf4] px-4 py-4">
                        <div className="flex items-center gap-2">
                            <Euro className="size-4 text-white" />
                            <span className="text-[11px] font-black uppercase tracking-wide text-white">
                                {intl.formatMessage("booking.messages.confirmation.amount")}
                            </span>
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <p className="text-[20px] font-black leading-[30px] text-white">{application.amount}€</p>
                            <p className="text-[11px] font-medium text-white/80">
                                {Number(application.hourly_rate).toFixed(2)}€/heure
                            </p>
                        </div>
                    </div>
                </div>

                {/* CESU summary — employer only */}
                {(isEmployerView && (isAccepted || isPending) && !isBookingCancelled) ? (
                    <div className="flex flex-col gap-3 rounded-2xl border-2 border-[#c6d2ff] bg-white px-4 py-4">
                        <div className="flex items-center gap-2">
                            <Shield className="size-5 shrink-0 text-[#4f39f6]" />
                            <span className="text-[13px] font-black text-[#4f39f6]">
                                {intl.formatMessage("booking.messages.confirmation.cesu")}
                            </span>
                            <CheckCircle2 className="size-4 shrink-0 text-[#4f39f6]" />
                        </div>

                        <dl className="flex flex-col gap-1.5 text-[12px] text-[#372aac]">
                        <div className="flex items-center justify-between gap-3">
                            <dt>{intl.formatMessage("booking.messages.confirmation.cesu_salary")}</dt>
                            <dd className="font-bold text-[#1a1c2d]">{formatEuro(pricing.salaryAmount)}</dd>
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <div className="flex items-center justify-between gap-3">
                                <dt>{intl.formatMessage("booking.messages.confirmation.cesu_management_fees")}</dt>
                                <dd className="font-medium text-[#1a1c2d]">
                                    {formatEuro(pricing.managementFees)}
                                </dd>
                            </div>
                            <dd className="text-[11px] leading-[16px] text-[#6b7280]">
                                {intl.formatMessage("booking.messages.confirmation.cesu_management_fees_hint")}
                            </dd>
                        </div>
                        {pricing.hasContributions && (
                            <>
                                <div className="flex items-center justify-between gap-3 border-t border-[#e0e7ff] pt-1.5">
                                    <dt>
                                        {intl.formatMessage(
                                            "booking.messages.confirmation.cesu_employer_contributions",
                                        )}
                                    </dt>
                                    <dd className="font-medium text-[#1a1c2d]">
                                        {formatEuro(pricing.employerContributions)}
                                    </dd>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <dt>
                                        {intl.formatMessage(
                                            "booking.messages.confirmation.cesu_employee_contributions",
                                        )}
                                    </dt>
                                    <dd className="font-medium text-[#1a1c2d]">
                                        {formatEuro(pricing.employeeContributions)}
                                    </dd>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <dt className="font-bold">
                                        {intl.formatMessage(
                                            "booking.messages.confirmation.cesu_social_contributions_total",
                                        )}
                                    </dt>
                                    <dd className="font-bold text-[#1a1c2d]">
                                        {formatEuro(pricing.socialContributions)}
                                    </dd>
                                </div>
                                <dd className="text-[11px] leading-[16px] text-[#6b7280]">
                                    {intl.formatMessage(
                                        "booking.messages.confirmation.cesu_social_contributions_hint",
                                    )}
                                </dd>
                            </>
                        )}
                    </dl>

                    <div className="rounded-xl border border-[#e0e7ff] bg-[#f8faff] px-3 py-2.5">
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-[12px] font-bold text-[#312c85]">
                                {intl.formatMessage("booking.messages.confirmation.cesu_amount_to_pay_now")}
                            </span>
                            <span className="text-[15px] font-black text-[#4f39f6]">
                                {formatEuro(pricing.amountToPayNow)}
                            </span>
                        </div>
                        {pricing.cesuPlusSelected && (
                            <p className="mt-1.5 text-[11px] leading-[16px] text-[#6b7280]">
                                {intl.formatMessage("booking.messages.confirmation.cesu_urssaf_debit_note")}
                            </p>
                        )}
                    </div>

                    <div className="flex items-center justify-between gap-3 border-t border-[#e0e7ff] pt-2">
                        <span className="text-[12px] font-medium text-[#372aac]">
                            {intl.formatMessage("booking.messages.confirmation.cesu_net_final_cost")}
                        </span>
                        <span className="text-[13px] font-black text-[#4f39f6]">
                            {formatEuro(pricing.netFinalCost)}
                        </span>
                    </div>
                    {pricing.hasContributions && (
                        <p className="text-[11px] leading-[16px] text-[#6b7280]">
                            {intl.formatMessage("booking.messages.confirmation.cesu_tax_credit")}{" "}
                            <span className="font-bold text-[#372aac]">{formatEuro(pricing.taxCredit)}</span>
                            {pricing.cesuPlusSelected || pricing.aiciActive
                                ? ` (${intl.formatMessage("booking.messages.confirmation.cesu_tax_credit_immediate")})`
                                : ` (${intl.formatMessage("booking.messages.confirmation.cesu_tax_credit_later")})`}
                        </p>
                    )}
                    </div>
                ) : (
                    <div className="flex h-12 items-center justify-center gap-2 rounded-2xl border-2 border-[#c6d2ff] bg-white px-4">
                        <Shield className="size-5 text-[#4f39f6]" />
                        <span className="text-[13px] font-black text-[#4f39f6]">
                            {intl.formatMessage("booking.messages.confirmation.cesu")}
                        </span>
                        <CheckCircle2 className="size-4 text-[#4f39f6]" />
                    </div>
                )}

                {/* CESU+ / AICI activation — client side when pending or after acceptance with CESU+ */}
                {isEmployerView && (canAct || (isAccepted && pricing.cesuPlusSelected)) && !isBookingCancelled && (
                    <div className="flex flex-col gap-3 rounded-2xl border border-[#e0e7ff] bg-[#f8faff] p-4">
                        <h3 className="text-[13px] font-bold leading-[19.5px] text-[#312c85]">
                            Gestion du{" "}
                            <a
                                href="https://www.cesu.urssaf.fr/info/accueil/utiliser-le-cesu/le-service-cesu/le-cesu--quest-ce-que-cest.html"
                                target="_blank"
                                rel="noreferrer"
                                className="text-[#4c4cf4] underline"
                            >
                                CESU+
                            </a>{" "}
                            et de{" "}
                            <a
                                href="https://www.cesu.urssaf.fr/info/accueil/beneficier-d-avantages/lavantage-fiscal/cesu-avance-immediate-quest-ce-q.html"
                                target="_blank"
                                rel="noreferrer"
                                className="text-[#4c4cf4] underline"
                            >
                                l&apos;AICI
                            </a>
                        </h3>

                        {isAccepted && pricing.cesuPlusSelected && (
                            <>
                                <p className="flex items-start gap-1 text-[12px] text-[#007a55]">
                                    <CheckCircle2 className="mt-0.5 size-3.5 flex-shrink-0" />
                                    Service <strong>CESU+</strong> activé avec ce prestataire.
                                </p>
                                {pricing.aiciActive && (
                                    <p className="flex items-start gap-1 text-[12px] text-[#007a55]">
                                        <CheckCircle2 className="mt-0.5 size-3.5 flex-shrink-0" />
                                        Service <strong>Avance Immédiate de Crédit d&apos;Impôt</strong> activé sur
                                        votre compte Cesu.
                                    </p>
                                )}
                            </>
                        )}

                        {canAct && hasCesuValid && (
                            <>
                                <input
                                    type="hidden"
                                    name="cesu_plus_status"
                                    value={cesuPlusStatus ? "1" : "0"}
                                    readOnly
                                />
                                {needsCesuChoice ? (
                                    <div className="flex flex-col gap-3">
                                        <div className="flex items-start gap-2">
                                            <p className="text-[12px] font-medium text-[#372aac]">
                                                {hasAiciActivated
                                                    ? intl.formatMessage("our_services.form.label.activate_cesu_plus")
                                                    : intl.formatMessage(
                                                          "our_services.form.label.activate_cesu_plus_aici",
                                                      )}
                                            </p>
                                            <TooltipProvider delayDuration={100}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            className="shrink-0 rounded-full p-0.5 text-[#4c4cf4] transition-colors hover:bg-[#eef2ff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4c4cf4]/30"
                                                            aria-label={intl.formatMessage(
                                                                "our_services.form.label.activate_cesu_plus.tooltip",
                                                            )}
                                                        >
                                                            <Info className="size-4" />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent
                                                        side="top"
                                                        className="max-w-[280px] border-[#e0e7ff] bg-white px-3 py-2 text-[11px] font-normal leading-[16px] text-[#372aac] shadow-lg"
                                                    >
                                                        {intl.formatMessage(
                                                            "our_services.form.label.activate_cesu_plus.tooltip",
                                                        )}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-5">
                                            <Radio
                                                name="activate_cesu_plus"
                                                value={activateCesuPlusChoice}
                                                required
                                                onChange={(e) => setActivateCesuPlusChoice(e.target.value)}
                                                options={[
                                                    {
                                                        value: "1",
                                                        label: intl.formatMessage("generic.label.yes"),
                                                    },
                                                    {
                                                        value: "0",
                                                        label: intl.formatMessage("generic.label.no"),
                                                    },
                                                ]}
                                                groupClassName="inline-flex items-center gap-2 rounded-xl border border-[#e0e7ff] bg-white px-3 py-1.5 transition-colors has-[:checked]:border-[#c6d2ff] has-[:checked]:bg-[#f8faff]"
                                                labelClassName="cursor-pointer text-[12px] font-bold text-[#372aac]"
                                                className="size-4 shrink-0 cursor-pointer accent-[#4c4cf4]"
                                            />
                                        </div>

                                        {!hasCesuPlusActivated && activateCesuPlusChoice === "1" && (
                                            <div className="flex flex-col gap-2">
                                                {!attestIsLoading && !attestIsGenerated && !attestHasError && (
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-[12px] text-[#ef4444]">
                                                            Attention, vous devez d'abord demander l'accord du
                                                            salarié prestataire au préalable&nbsp;!
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => void generateAttestation()}
                                                            className="inline-flex items-center gap-1 text-left text-[12px] font-medium text-[#4c4cf4] underline"
                                                        >
                                                            Générer l'attestation d'adhésion CESU+ à signer
                                                            <Download className="size-3" />
                                                        </button>
                                                    </div>
                                                )}
                                                {attestIsLoading && (
                                                    <div className="flex items-center gap-2 text-[12px] text-[#6b7280]">
                                                        <span>Génération en cours...</span>
                                                        <Loader2 className="size-3 animate-spin" />
                                                    </div>
                                                )}
                                                {attestIsGenerated && (
                                                    <p className="flex items-start gap-1 text-[12px] text-[#007a55]">
                                                        <CheckCircle2 className="mt-0.5 size-3.5 flex-shrink-0" />
                                                        Attestation générée, un e-mail vous a été envoyé ainsi qu'au
                                                        salarié pour procéder à la signature électronique du document.
                                                    </p>
                                                )}
                                                {attestHasError && (
                                                    <p className="text-[12px] text-[#ef4444]">
                                                        {intl.formatMessage("generic.error")}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div>
                                        <p className="flex items-start gap-1 text-[12px] text-[#007a55]">
                                            <CheckCircle2 className="mt-0.5 size-3.5 flex-shrink-0" />
                                            Service <strong>CESU+</strong> déjà activé avec ce prestataire.
                                        </p>
                                        <p className="mt-1 text-[11px] text-[#6b7280]">
                                            {intl.formatMessage(
                                                "booking.messages.confirmation.cesu_plus_deactivate_hint",
                                            )}
                                        </p>
                                    </div>
                                )}

                                {hasAiciActivated && (
                                    <p className="flex items-start gap-1 text-[12px] text-[#007a55]">
                                        <CheckCircle2 className="mt-0.5 size-3.5 flex-shrink-0" />
                                        Service <strong>Avance Immédiate de Crédit d'Impôt</strong> déjà activé sur
                                        votre compte Cesu.
                                    </p>
                                )}
                            </>
                        )}

                        {canAct && hasErrCesu && (
                            <p className="text-[12px] text-[#6b7280]">
                                Pour pouvoir activer le service{" "}
                                <a
                                    href="https://www.cesu.urssaf.fr/info/accueil/utiliser-le-cesu/le-service-cesu/le-cesu--quest-ce-que-cest.html"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-bold underline"
                                >
                                    CESU+
                                </a>{" "}
                                avec ce salarié, vous devez l'avoir déjà employé au préalable, ou l'ajouter sur
                                votre{" "}
                                <a
                                    href="https://www.cesu.urssaf.fr/decla/index.html?page=page_empl_mes_salaries"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-bold underline"
                                >
                                    compte CESU
                                </a>{" "}
                                (rubrique &quot;Gérer mon compte&quot; -&gt; &quot;Mes salariés&quot;).
                            </p>
                        )}

                        {canAct && hasErrCesuSalarie && (
                            <p className="text-[12px] italic text-[#6b7280]">
                                Ce salarié n'a pas encore de n° CESU, il le recevra par courrier après sa 1ère
                                prestation.
                            </p>
                        )}
                    </div>
                )}

                {/* Action buttons — client side only when pending */}
                {canAct && (
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onRefuse}
                            disabled={isAccepting}
                            className="flex-1 rounded-2xl border border-[#e5e7eb] bg-white py-3 text-[13px] font-black text-[#6b7280] transition-colors hover:border-[#f87171] hover:text-[#ef4444] disabled:opacity-50"
                        >
                            {intl.formatMessage("booking.messages.confirmation.action_refuse")}
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleAcceptClick()}
                            disabled={!canSubmitAccept}
                            className="flex-1 rounded-2xl bg-[#4c4cf4] py-3 text-[13px] font-black text-white transition-colors hover:bg-[#3b3bd3] disabled:opacity-50"
                        >
                            {isAccepting ? (
                                <span className="inline-flex items-center justify-center gap-2">
                                    <Loader2 className="size-4 animate-spin" />
                                    {intl.formatMessage("booking.messages.confirmation.action_accept")}
                                </span>
                            ) : (
                                intl.formatMessage("booking.messages.confirmation.action_accept")
                            )}
                        </button>
                    </div>
                )}

                {/* Next steps — confirmed only */}
                {isAccepted && !isBookingCancelled && (
                    <div className="rounded-2xl border border-[#e0e7ff] bg-[#eef2ff] p-4">
                        <p className="mb-2 text-[12px] font-black text-[#312c85]">
                            📋 {intl.formatMessage("booking.messages.confirmation.next_steps_title")}
                        </p>
                        <ul className="flex flex-col gap-1">
                            {isEmployerView && !isPaid && (
                                <li className="text-[12px] font-normal text-[#372aac]">
                                    • {intl.formatMessage("booking.messages.confirmation.next_step_1")}
                                </li>
                            )}
                            <li className="text-[12px] font-normal text-[#372aac]">
                                • {intl.formatMessage("booking.messages.confirmation.next_step_2")}
                            </li>
                            <li className="text-[12px] font-normal text-[#372aac]">
                                • {intl.formatMessage("booking.messages.confirmation.next_step_3")}
                            </li>
                            <li className="text-[12px] font-normal text-[#372aac]">
                                •{" "}
                                {intl.formatMessage("booking.messages.confirmation.next_step_4", {
                                    link: (chunks) => (
                                        <Link
                                            to={ROUTES.SERVICE_HISTORY}
                                            className="font-medium text-[#4c4cf4] underline"
                                        >
                                            {chunks}
                                        </Link>
                                    ),
                                })}
                            </li>
                        </ul>
                        {showPayButton && (
                            <button
                                type="button"
                                onClick={() => setIsPaymentModalOpen(true)}
                                className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#00bc7d] py-3 text-[14px] font-black text-white shadow-md transition-colors hover:bg-[#00a36d] active:bg-[#009960]"
                            >
                                <CreditCard className="size-4" />
                                {intl.formatMessage("booking.messages.confirmation.action_pay")}
                                <span className="opacity-90">· {formatEuro(pricing.amountToPayNow)}</span>
                            </button>
                        )}
                        {isPaid && isEmployerView && (
                            <p className="mt-3 flex items-center gap-1 text-[12px] font-medium text-[#007a55]">
                                <CheckCircle2 className="size-3.5" />
                                {intl.formatMessage("booking.messages.confirmation.payment.paid")}
                            </p>
                        )}
                    </div>
                )}

                {showPayButton && bookingId && providerId && clientId && (
                    <OfferPaymentModal
                        open={isPaymentModalOpen}
                        onOpenChange={setIsPaymentModalOpen}
                        bookingId={bookingId}
                        providerId={providerId}
                        clientId={clientId}
                        hourlyRate={Number(application.hourly_rate)}
                        nbHours={Number(application.duration)}
                        cesuPlusActive={pricing.cesuPlusSelected}
                        amountToPay={pricing.amountToPayNow}
                        onSuccess={() => void handlePaymentSuccess()}
                    />
                )}
            </div>
        </div>
    );
};

export default BookingConfirmationCard;
